import { useState } from 'react';
import Select from 'react-select-2';
import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';
import type { ValueType } from 'react-select-2/lib/types';

import Banner from '../../generic/Banner';
import { BannerStyle } from '../../../../typings/enums';
import Flex from '../../generic/Flex';
import { InlineLoaderButton } from '../../generic';
import { post } from '../../../redux/api';
import { useDialogContext } from '../../../contexts/DialogContext';
import { useAsyncStatus } from '../../../hooks';
import { thirdPartyFetchersMap } from '../../../../shared/fetchers';
import { selectOptionFactory } from '../../../utils';
import type { FailedFetchResponse } from '../../../helpers/errors';

const thirdPartyFetchers: string[] = Object.keys(thirdPartyFetchersMap);

function isConflictResponse(res: any) {
  return res?.status === 409;
}

const RecalculateVulnsComponent: VoidFunctionComponent = () => {
  const { onConfirm } = useDialogContext();
  const [fetcher, setFetcher] = useState<string>(_.head(thirdPartyFetchers) ?? '');
  const [isShowingConflictAlert, setIsShowingConflictAlert] = useState(false);
  const { status, onPending, onSuccess, onError } = useAsyncStatus();

  const submitEnrichment = async () => {
    onPending();
    try {
      await post(`/api/vulns/recalculate`, { fetcher });
      setIsShowingConflictAlert(false);
      onSuccess();
    } catch (err) {
      if (isConflictResponse(err)) {
        setIsShowingConflictAlert(true);
        onSuccess();
        return;
      }

      onError(err as FailedFetchResponse);
    }
  };

  return (
    <div className="card">
      <h3 className="card-title padding-bottom-sm">
        Recalculate Vulns for Fetcher
      </h3>
      {isShowingConflictAlert && (
        <div className="padding-bottom-md">
          <Banner
            isDismissible
            onDismiss={() => setIsShowingConflictAlert(false)}
            title="Failed"
            body="A recalculation job already exists for this fetcher. Try again later."
            bannerStyle={BannerStyle.Medium}
          />
        </div>
      )}
      {status.error && (
        <div className="padding-bottom-md">
          <Banner
            isDismissible
            onDismiss={() => setIsShowingConflictAlert(false)}
            title="Failed"
            body={status.error}
            bannerStyle={BannerStyle.High}
          />
        </div>
      )}
      <Flex justify="left" gap={10}>
        <div className="width-quarter">
          <Select
            value={selectOptionFactory(fetcher)}
            options={thirdPartyFetchers.map((f) => selectOptionFactory(f))}
            onChange={(option: ValueType<{ value: string; label: string }>) => {
              setFetcher(option?.value);
            }}
          />
        </div>
        <InlineLoaderButton
          onClick={() => {
            onConfirm({
              title: 'Are you sure?',
              body: 'Vulnerability recalculation can be a long running process, depending on how many vulns exist for the fetcher. Are you sure you want to proceed?',
              onConfirm: submitEnrichment,
            });
          }}
          isLoading={status.loading}
          isDisabled={status.loading}
        >
          Submit
        </InlineLoaderButton>
      </Flex>
    </div>
  );
};

export default RecalculateVulnsComponent;
